import React, { useEffect, useState } from 'react';
import { ARTIFACT_ID } from '../../constants';
import { useInputHelper } from 'framework/form';
import { InputLoader, SmallLoader } from 'framework/ui/mui-builder';
import { Button } from 'framework/ui/mui-builder';
import useArtifactHelper from './artifactHelper';
import useConvertingTagsHelper from './artifactTagsConverted';
import useArtifactsGarbageCollector from './artifactsGarbageCollector';
import { Box } from '@mui/material';
import { parseHtmlInert } from 'domain/shared/artifact/parseHtmlInert';

export type WysiwygInputPasteArtifactDecoratorProps = {
  children: React.ReactElement;
  artifactsSource: string;
  artifactTypeId?: number;
  isRiskService?: boolean;
};

const WysiwygInputPasteArtifactDecorator: React.FC<WysiwygInputPasteArtifactDecoratorProps> = ({
  children,
  artifactsSource,
  artifactTypeId = ARTIFACT_ID.NOTE_ATTACHMENT,
  isRiskService = false,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [preparedValue, setPreparedValue] = useState('');

  const { getArtifactsDataByIds, uploadArtifact, isLoading, getThumbnail, cancel, validate, getArtifacts } =
    useArtifactHelper({
      artifactTypeId,
      artifactsSource,
      isRiskService,
    });

  const { getValue, getOnChangeCallback, getRawOnChangeCallback, setFormFieldErrors } = useInputHelper(children.props);

  const onChangeCallback = getOnChangeCallback();
  const { source } = children.props;

  const { replaceImagesWithArtifactTag, replaceArtifactWithImageTag } = useConvertingTagsHelper();

  const { setRefreshData } = useArtifactsGarbageCollector({
    artifactsIds: getArtifacts(),
    onChangeCallback: getRawOnChangeCallback() || (() => ({})),
    source,
    editorContent: getValue(),
  });

  const handlePaste = async (e: { files: any; content: any }) => {
    const { files, content } = e;

    if (files.length && !isLoading) {
      const error = validate(files);

      const areValidArtifacts = !error;

      if (areValidArtifacts) {
        const [artifactId, artifactSrc] = await uploadArtifact(files[0]);
        return `<img data-artifact-id="${artifactId}" src="${artifactSrc}" alt="" />`;
      } else {
        setFormFieldErrors([error]);
      }
    } else if (!isLoading) {
      return content;
    }

    return null;
  };

  const handleChange = (data: { [x: string]: any }) => {
    const value = data[source];

    const div = parseHtmlInert(value);
    const images = div.querySelectorAll('img[data-artifact-id]');

    replaceImagesWithArtifactTag(images);

    onChangeCallback({ target: { value: div.innerHTML } });
  };

  // converting artifact tags to img on mount from initial html-string value
  useEffect(() => {
    (async () => {
      const value = getValue();
      const div = parseHtmlInert(value as string);
      await replaceArtifactWithImageTag(div.querySelectorAll('artifact'), getArtifactsDataByIds, getThumbnail);

      setPreparedValue(div.innerHTML);
      setIsInitialized(true);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box position="relative">
      {isLoading && (
        <>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              zIndex: 100,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: 'common.softBorder',
              pointerEvents: 'none',
            }}
          >
            <SmallLoader />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: '-40px',
              zIndex: 101,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              '& button': { padding: '2px 20px' },
            }}
          >
            <Button
              onClick={() => {
                cancel();
              }}
              data-testid="decorator-cancel-button"
            >
              Cancel uploading
            </Button>
          </Box>
        </>
      )}
      {isInitialized ? (
        React.cloneElement(children, {
          onPasteCallback: handlePaste,
          onChangeCallback: handleChange,
          disabled: isLoading || children.props.disabled,
          value: preparedValue,
          setRefreshDataCallback: setRefreshData,
          helpText:
            'Press CTRL+V to insert the image into the text from the clipboard.' +
            '\n Notice that the clipboard image may be larger than the original image file itself.',
          errorMessages: isLoading ? [] : undefined,
          // override errorMessages from the context during uploading
        })
      ) : (
        <InputLoader />
      )}
    </Box>
  );
};

export default WysiwygInputPasteArtifactDecorator;
